/**
 * Model Configuration Index
 * Central export for all 10 business model configurations
 * 
 * PHASE 3 AUDIT COMPLETED ✅
 * All models verified against backend default assumptions
 * 
 * FIELD COUNT (including automaticCapitalRaise):
 * ✅ SaaS - 45 fields (12 groups)
 * ✅ E-commerce - 46 fields (8 groups)
 * ✅ Restaurant - 48 fields (12 groups)
 * ✅ Marketplace - 53 fields (14 groups)
 * ✅ Manufacturing - 66 fields (18 groups)
 * ✅ Consulting - 51 fields (15 groups)
 * ✅ Retail - 56 fields (16 groups)
 * ✅ Cloud Kitchen - 41 fields (16 groups)
 * ✅ FinTech - 53 fields (14 groups)
 * ✅ Healthcare - 14 fields (7 groups)
 * 
 * TOTAL: ~470+ assumption fields extracted
 * 
 * CRITICAL FIX APPLIED:
 * - Added `automaticCapitalRaise` (checkbox) to all 10 models
 * - This field enables automatic capital raising when cash goes negative
 * - Verified against backend calculations that use this field
 */

// Model configurations
export { saasConfig, saasDefaultAssumptions } from './saas.config';
export { ecommerceConfig, ecommerceDefaultAssumptions } from './ecommerce.config';
export { restaurantConfig, restaurantDefaultAssumptions } from './restaurant.config';
export { marketplaceConfig, marketplaceDefaultAssumptions } from './marketplace.config';
export { manufacturingConfig, manufacturingDefaultAssumptions } from './manufacturing.config';
export { consultingConfig, consultingDefaultAssumptions } from './consulting.config';
export { retailConfig, retailDefaultAssumptions } from './retail.config';
export { cloudkitchenConfig, cloudkitchenDefaultAssumptions } from './cloudkitchen.config';
export { fintechConfig, fintechDefaultAssumptions } from './fintech.config';
export { healthcareConfig, healthcareDefaultAssumptions } from './healthcare.config';

// Import all configs for registry
import { saasConfig } from './saas.config';
import { ecommerceConfig } from './ecommerce.config';
import { restaurantConfig } from './restaurant.config';
import { marketplaceConfig } from './marketplace.config';
import { manufacturingConfig } from './manufacturing.config';
import { consultingConfig } from './consulting.config';
import { retailConfig } from './retail.config';
import { cloudkitchenConfig } from './cloudkitchen.config';
import { fintechConfig } from './fintech.config';
import { healthcareConfig } from './healthcare.config';
import type { ModelConfig } from '@/types/models';

// Registry of all available models
export const MODEL_REGISTRY: Record<string, ModelConfig> = {
  saas: saasConfig,
  ecommerce: ecommerceConfig,
  restaurant: restaurantConfig,
  marketplace: marketplaceConfig,
  manufacturing: manufacturingConfig,
  consulting: consultingConfig,
  retail: retailConfig,
  cloudkitchen: cloudkitchenConfig,
  fintech: fintechConfig,
  healthcare: healthcareConfig,
};

// List of all model types for navigation
export const MODEL_TYPES = [
  { id: 'saas', name: 'SaaS', icon: '💻', color: '#3b82f6', description: 'Subscription-based software' },
  { id: 'ecommerce', name: 'E-commerce', icon: '🛒', color: '#7c3aed', description: 'Online retail business' },
  { id: 'restaurant', name: 'Restaurant', icon: '🍽️', color: '#f97316', description: 'Dine-in restaurant' },
  { id: 'marketplace', name: 'Marketplace', icon: '🏪', color: '#06b6d4', description: 'Two-sided marketplace' },
  { id: 'manufacturing', name: 'Manufacturing', icon: '🏭', color: '#6366f1', description: 'Production & manufacturing' },
  { id: 'consulting', name: 'Consulting', icon: '💼', color: '#14b8a6', description: 'Professional services' },
  { id: 'retail', name: 'Retail', icon: '🏬', color: '#ec4899', description: 'Physical retail store' },
  { id: 'cloudkitchen', name: 'Cloud Kitchen', icon: '🍳', color: '#f59e0b', description: 'Delivery-only kitchen' },
  { id: 'fintech', name: 'FinTech', icon: '💳', color: '#22c55e', description: 'Financial technology' },
  { id: 'healthcare', name: 'Healthcare', icon: '🏥', color: '#ef4444', description: 'Medical practice' },
] as const;

// Helper to get model config by type
export function getModelConfig(modelType: string): ModelConfig | undefined {
  return MODEL_REGISTRY[modelType];
}

// Helper to check if a model type is valid
export function isValidModelType(modelType: string): boolean {
  return MODEL_TYPES.some(m => m.id === modelType);
}
